import React, { useEffect, useRef, useState } from 'react';
import { buildApiUrl } from '../services/apiConfig';
import { useSpecState } from '../ui-spec-renderer/SpecStateContext';

const EMBED_API = buildApiUrl('CHART', '/api/embed-new');
const SQL_ASK_API = buildApiUrl('SQL_AGENT', '/sql-ask');

// Returns true if the prompt looks like a natural-language request rather than
// a technical chart spec (which contains words like "chart", "x=", "y=", etc.)
function isNaturalLanguageQuery(prompt) {
  if (!prompt) return false;
  const lower = prompt.toLowerCase();
  const techMarkers = ['x=', 'y=', 'hover showing', 'gridlines'];
  return !techMarkers.some((m) => lower.includes(m));
}

function applyChartStyles(html) {
  return html.replace(
    /(<head[^>]*>)/i,
    `$1<meta name="viewport" content="width=device-width, initial-scale=1.0"><style>
      * { box-sizing: border-box; }
      html { margin: 0 !important; padding: 0 !important; width: 100% !important; height: 100% !important; background: white !important; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important; overflow-x: hidden !important; }
      body { margin: 0 !important; padding: 15px !important; width: 100% !important; min-height: 100% !important; background: white !important; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important; overflow: visible !important; }
      .plotly-graph-div, .js-plotly-plot, #plotly-div, [id*="plotly"] { width: 100% !important; height: 100% !important; min-height: 380px !important; }
      .main-svg { width: 100% !important; height: 100% !important; }
    </style>`
  );
}

const Spinner = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: 'chart-spin 0.8s linear infinite' }}
    aria-hidden="true"
  >
    <style>{`@keyframes chart-spin { to { transform: rotate(360deg); } }`}</style>
    <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="3" />
    <path d="M12 2a10 10 0 0 1 10 10" stroke="#6b7280" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

// When SQL returns columns with different names than the static chartPrompt expects
// (e.g. prompt says x=date but actual column is transaction_date), rename matching
// columns so the chart API receives the expected names without changing the prompt.
function alignColumnsToPrompt(rows, prompt) {
  if (!rows.length) return rows;
  const actual = Object.keys(rows[0]);
  const xMatch = prompt.match(/\bx=(\w+)/i);
  const yMatch = prompt.match(/\by=(\w+)/i);
  if (!xMatch && !yMatch) return rows;

  const xExp = xMatch?.[1];
  const yExp = yMatch?.[1];

  const row0 = rows[0];
  const isNumeric = (k) => {
    const v = row0[k];
    return typeof v === 'number' || (typeof v === 'string' && v !== '' && !isNaN(Number(v)));
  };
  const numericCols = actual.filter(isNumeric);
  const stringCols  = actual.filter((k) => !isNumeric(k));

  const rename = {};
  const usedActual   = new Set();
  const usedExpected = new Set();

  const tryAssign = (exp, candidates) => {
    if (!exp || actual.includes(exp) || usedExpected.has(exp)) return;
    let pick = candidates.find(
      (a) => !usedActual.has(a) && (
        a.toLowerCase().includes(exp.toLowerCase()) ||
        exp.toLowerCase().includes(a.toLowerCase())
      )
    );
    if (!pick) pick = candidates.find((a) => !usedActual.has(a));
    if (pick) {
      rename[pick] = exp;
      usedActual.add(pick);
      usedExpected.add(exp);
    }
  };

  tryAssign(yExp, numericCols.length ? numericCols : actual);
  tryAssign(xExp, stringCols.length  ? stringCols  : actual);

  if (!Object.keys(rename).length) return rows;
  return rows.map((row) => {
    const out = {};
    for (const [k, v] of Object.entries(row)) out[rename[k] ?? k] = v;
    return out;
  });
}

const Chart = ({
  data,
  chartPrompt,
  title,
  height = 380,
  className,
  style,
  onLoad,
  onError,
  onDataChange,
  defaultQuestion,
}) => {
  const specState = useSpecState();
  const iframeRef = useRef(null);
  const [chartSrc, setChartSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sqlLoading, setSqlLoading] = useState(false);
  const [sqlError, setSqlError] = useState(null);
  // sqlData holds rows returned by the SQL agent; overrides the static data prop.
  const [sqlData, setSqlData] = useState(null);

  const [activePrompt, setActivePrompt] = useState(chartPrompt);
  const [editingPrompt, setEditingPrompt] = useState(chartPrompt || '');
  const [isEditing, setIsEditing] = useState(false);

  // Keep activePrompt in sync if the prop changes externally (new render accepted)
  useEffect(() => {
    setActivePrompt(chartPrompt);
    setEditingPrompt(chartPrompt || '');
    setSqlData(null);
  }, [chartPrompt]);

  // Auto-load real data on mount when defaultQuestion is provided
  useEffect(() => {
    if (!defaultQuestion) return;
    let cancelled = false;
    const runDefaultQuery = async () => {
      setSqlLoading(true);
      try {
        const userId = sessionStorage.getItem('user_id') || localStorage.getItem('userId') || '';
        const form = new FormData();
        form.append('question', defaultQuestion);
        form.append('return_raw', 'true');
        if (specState.use_case_id) form.append('use_case_id', specState.use_case_id);
        if (specState.agent_id)    form.append('agent_id',    specState.agent_id);
        const advisorId = userId || specState.advisor_id;
        if (advisorId) form.append('advisor_id', advisorId);
        if (userId)    form.append('user_id',    userId);
        if (specState.cust_id)   form.append('cust_id',   specState.cust_id);
        if (specState.domain_id) form.append('domain_id', specState.domain_id);

        const res = await fetch(SQL_ASK_API, { method: 'POST', body: form });
        if (!res.ok || cancelled) return;
        const json = await res.json();
        const rawResult = json?.response?.result;
        const rows = typeof rawResult === 'string' ? JSON.parse(rawResult) : rawResult;
        if (!cancelled && Array.isArray(rows) && rows.length > 0) {
          setSqlData(rows);
          onDataChange?.(rows);
        }
      } catch (_) {
        // silent — default load is best-effort
      } finally {
        if (!cancelled) setSqlLoading(false);
      }
    };
    runDefaultQuery();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultQuestion]);

  const activeData = sqlData || data;

  useEffect(() => {
    if (!activeData || !activePrompt) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const primaryColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-primary')
      .trim();
    const finalColor = primaryColor.startsWith('#') ? primaryColor : `#${primaryColor}`;

    const rows = Array.isArray(activeData) ? activeData : [];
    const alignedRows = alignColumnsToPrompt(rows, activePrompt);
    const tailwindConfig = sessionStorage.getItem('tailwindConfig') || null;

    fetch(EMBED_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        input: JSON.stringify(alignedRows),
        query: activePrompt,
        primary_color: finalColor,
        ...(tailwindConfig ? { tailwind_config: tailwindConfig } : {}),
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Chart agent returned ${res.status}`);
        return res.text();
      })
      .then((html) => {
        if (cancelled) return;
        const styled = applyChartStyles(html);
        const blob = new Blob([styled], { type: 'text/html' });
        setChartSrc(URL.createObjectURL(blob));
        onLoad?.();
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err.message || 'Failed to generate chart';
        setError(msg);
        onError?.(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeData, activePrompt]);

  useEffect(() => {
    return () => {
      if (chartSrc) URL.revokeObjectURL(chartSrc);
    };
  }, [chartSrc]);

  const handleOpenEdit = () => {
    setEditingPrompt(activePrompt);
    setIsEditing((v) => !v);
  };

  const handleApply = async () => {
    const trimmed = editingPrompt.trim();
    if (!trimmed) { setIsEditing(false); return; }

    if (isNaturalLanguageQuery(trimmed)) {
      setSqlLoading(true);
      setSqlError(null);
      setIsEditing(false);
      try {
        const userId = sessionStorage.getItem('user_id') || localStorage.getItem('userId') || '';
        const form = new FormData();
        form.append('question', trimmed);
        form.append('return_raw', 'true');
        if (specState.use_case_id) form.append('use_case_id', specState.use_case_id);
        if (specState.agent_id)    form.append('agent_id',    specState.agent_id);
        const advisorId = userId || specState.advisor_id;
        if (advisorId)             form.append('advisor_id',  advisorId);
        if (userId)                form.append('user_id',     userId);
        if (specState.cust_id)     form.append('cust_id',     specState.cust_id);
        if (specState.domain_id)   form.append('domain_id',   specState.domain_id);

        const res = await fetch(SQL_ASK_API, { method: 'POST', body: form });
        if (!res.ok) throw new Error(`SQL agent returned ${res.status}`);
        const json = await res.json();
        const rawResult = json?.response?.result;
        const answer = json?.response?.answer;
        const rows = typeof rawResult === 'string' ? JSON.parse(rawResult) : rawResult;
        if (Array.isArray(rows) && rows.length > 0) {
          setSqlData(rows);
          onDataChange?.(rows);
          setSqlError(null);
          setActivePrompt(trimmed);
        } else {
          setSqlError(answer || 'No data found — try a different question');
        }
      } catch (err) {
        setSqlError(err.message || 'SQL agent failed');
      } finally {
        setSqlLoading(false);
      }
      return;
    }

    if (trimmed !== activePrompt) {
      setSqlData(null);
      setActivePrompt(trimmed);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditingPrompt(activePrompt);
    setIsEditing(false);
  };

  const wrapperStyle = {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
    background: 'white',
    ...style,
  };

  if (!data || !activePrompt) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height,
          background: '#f9fafb',
          borderRadius: 8,
          border: '1px dashed #d1d5db',
          ...style,
        }}
      >
        <span style={{ color: '#9ca3af', fontSize: 13 }}>No chart data</span>
      </div>
    );
  }

  if (loading && !chartSrc) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          height,
          background: '#f9fafb',
          borderRadius: 8,
          border: '1px solid #e5e7eb',
          ...style,
        }}
      >
        <Spinner />
        <span style={{ color: '#6b7280', fontSize: 13 }}>Generating chart…</span>
      </div>
    );
  }

  if (error && !chartSrc) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height,
          background: '#fef2f2',
          borderRadius: 8,
          border: '1px solid #fecaca',
          ...style,
        }}
      >
        <span style={{ color: '#dc2626', fontSize: 13 }}>{error}</span>
      </div>
    );
  }

  return (
    <div className={className} style={wrapperStyle}>
      {/* Title row + edit button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px 0', flexShrink: 0 }}>
        {title && (
          <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{title}</span>
        )}
        <button
          onClick={handleOpenEdit}
          title={isEditing ? 'Close prompt editor' : 'Edit chart prompt'}
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '3px 8px',
            fontSize: 12,
            color: isEditing ? '#1d4ed8' : '#6b7280',
            background: isEditing ? '#eff6ff' : 'transparent',
            border: '1px solid',
            borderColor: isEditing ? '#bfdbfe' : '#e5e7eb',
            borderRadius: 5,
            cursor: 'pointer',
          }}
        >
          <EditIcon />
          {isEditing ? 'Close' : 'Edit prompt'}
        </button>
      </div>

      {/* Inline prompt editor */}
      {isEditing && (
        <div style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', flexShrink: 0, background: '#f9fafb' }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Describe what you want to see
          </p>
          <textarea
            value={editingPrompt}
            onChange={(e) => setEditingPrompt(e.target.value)}
            rows={4}
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: 12,
              fontFamily: 'monospace',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              resize: 'vertical',
              outline: 'none',
              background: 'white',
              color: '#111827',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={handleCancel}
              style={{ padding: '4px 12px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 5, background: 'white', color: '#374151', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              style={{ padding: '4px 12px', fontSize: 12, border: 'none', borderRadius: 5, background: '#1d4ed8', color: 'white', cursor: 'pointer', fontWeight: 600 }}
            >
              Regenerate
            </button>
          </div>
        </div>
      )}

      {sqlLoading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#eff6ff', borderBottom: '1px solid #bfdbfe', flexShrink: 0 }}>
          <Spinner />
          <span style={{ fontSize: 12, color: '#1d4ed8' }}>Querying database…</span>
        </div>
      )}

      {sqlError && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#fef2f2', borderBottom: '1px solid #fecaca', flexShrink: 0 }}>
          <span style={{ fontSize: 12, color: '#dc2626' }}>{sqlError}</span>
          <button onClick={() => setSqlError(null)} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>✕</button>
        </div>
      )}

      {chartSrc && (
        <iframe
          ref={iframeRef}
          src={chartSrc}
          title={title || 'Chart'}
          style={{
            display: 'block',
            width: '100%',
            height: title ? height - 38 : height,
            border: 'none',
            background: 'white',
            flexShrink: 0,
          }}
        />
      )}
    </div>
  );
};

export default Chart;